import React, { useState } from 'react';
import { toast } from 'react-toastify';
import generator from 'generate-password-ts';

// Utils
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from '../../utils/passwordRules';

// Components
import Modal from '../Modal/Modal';
import InputControl from '../InputControl/InputControl';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import IconCopy from '../Icons/IconCopy/IconCopy';
import { ElementSize } from '../../types/element-size';

type ResetPasswordModalProps = {
  isVisible: boolean;
  memberName: string;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
};

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isVisible, memberName, onClose, onSubmit }) => {
  const [password, setPassword] = useState('');
  const [isCopiedPwd, setIsCopiedPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const resetState = () => {
    setPassword('');
    setIsCopiedPwd(false);
    setPasswordError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const generatePassword = () => {
    const generated = generator.generate({
      length: 12,
      numbers: true,
      symbols: true,
      uppercase: true,
      lowercase: true,
      strict: true,
    });

    setPassword(generated);
    setIsCopiedPwd(false);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setIsCopiedPwd(false);
    setPasswordError(null);
  };

  const handleCopy = async () => {
    if (!password) {
      toast.error('Password field is empty');
      return;
    }

    await navigator.clipboard.writeText(password);
    setIsCopiedPwd(true);
    toast.success('Password has been copied');
  };

  const handleAccept = async () => {
    if (!isCopiedPwd) {
      toast.error('You didn\'t copy the password!');
      return;
    }

    const validationError = getPasswordStrengthError(password);

    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      await onSubmit(password);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      modalTitle={`Reset password for ${memberName}`}
      isVisible={isVisible}
      onClose={handleClose}
      onAccept={handleAccept}
      acceptBtnText="Reset Password"
      showAcceptBtn
      showCancelBtn
      acceptBtnDisabled={submitting || !password}
      cancelBtnDisabled={submitting}
    >
      <div className="flex flex-col xsm:flex-row xsm:items-center gap-4">
        <div className="w-full max-w-96">
          <InputControl
            label="New Password"
            name="password"
            placeholder="Enter new password"
            value={password}
            customOnChange={(e) => handlePasswordChange(e.target.value)}
            iconRight={<IconCopy size={ElementSize.MD} onClick={handleCopy} fill="#d5d7db" />}
            showError={Boolean(passwordError)}
            errorText={passwordError ?? ''}
            suggestion={PASSWORD_RULES_HINT}
          />
        </div>
        <InteractiveButton className="min-h-13" text="Generate" onClick={generatePassword} />
      </div>
    </Modal>
  );
};

export default ResetPasswordModal;
